import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Accordion,
  AccordionItem,
  Alert,
  Button,
  Chip,
  Combobox,
  Field,
  Input,
  NotificationsIcon,
  SendIcon,
  Spinner,
  Switch,
  Text,
  Textarea,
} from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { useSnackbarStore } from '../../../shared/stores/snackbarStore';
import {
  getNotificationScenarios,
  getNotificationEntityOptions,
  triggerTestNotification,
  type NotificationTypeDto,
  type TriggerTestNotificationRequest,
  type EntityOptionDto,
  type NotificationEntityOptionsResponse,
} from '../api/notificationsApi';

function formatTypeName(typeName: string): string {
  if (typeName === '_Raw') return 'Raw Definition';
  return typeName
    .replace('NotificationV1', '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
}

function getRequiredInputLabel(requiredInput: string): {
  label: string;
  color: 'success' | 'info' | 'warning';
} {
  if (requiredInput === 'none')
    return { label: 'Current user', color: 'success' };
  if (requiredInput === 'raw')
    return { label: 'Custom payload', color: 'warning' };
  return { label: `Requires ${requiredInput}`, color: 'info' };
}

function getOptionsForInput(
  requiredInput: string,
  entityOptions: NotificationEntityOptionsResponse | null
): EntityOptionDto[] {
  if (!entityOptions) return [];
  if (requiredInput === 'pot') return entityOptions.pots;
  if (requiredInput === 'allocation') return entityOptions.allocations;
  if (requiredInput === 'deduction') return entityOptions.deductions;
  return [];
}

export const NotificationTestingPage: React.FC = () => {
  const [types, setTypes] = useState<NotificationTypeDto[]>([]);
  const [entityOptions, setEntityOptions] =
    useState<NotificationEntityOptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEntities, setSelectedEntities] = useState<
    Record<string, EntityOptionDto | null>
  >({});
  const [rawTypes, setRawTypes] = useState<Record<string, string>>({});
  const [rawData, setRawData] = useState<Record<string, string>>({});
  const [sendingType, setSendingType] = useState<string | null>(null);
  const [forceUniqueDedup, setForceUniqueDedup] = useState(true);
  const showSnackbar = useSnackbarStore(state => state.showSnackbar);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [typesResult, optionsResult] = await Promise.all([
        getNotificationScenarios(),
        getNotificationEntityOptions(),
      ]);

      setTypes(typesResult);
      setEntityOptions(optionsResult);
    } catch (err) {
      setError(getErrorMessage(err));
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTrigger = async (type: NotificationTypeDto) => {
    setSendingType(type.notificationType);

    const request: TriggerTestNotificationRequest = {
      notificationType: type.notificationType,
      forceUniqueDedup,
    };

    const selected = selectedEntities[type.notificationType];

    if (type.requiredInput === 'pot') request.potId = selected?.id;
    else if (type.requiredInput === 'allocation')
      request.allocationId = selected?.id;
    else if (type.requiredInput === 'deduction')
      request.deductionId = selected?.id;
    else if (type.requiredInput === 'raw') {
      request.rawNotificationType = rawTypes[type.notificationType];
      request.rawData = rawData[type.notificationType] || undefined;
    }

    try {
      const result = await triggerTestNotification(request);
      setSendingType(null);

      showSnackbar(result.message || 'Notification sent!', 'success');
    } catch (err) {
      setSendingType(null);

      showSnackbar(getErrorMessage(err), 'error');
    }
  };

  const isSendDisabled = (type: NotificationTypeDto) => {
    if (sendingType !== null) return true;
    if (type.requiredInput === 'none') return false;
    if (type.requiredInput === 'raw')
      return !rawTypes[type.notificationType]?.trim();
    return !selectedEntities[type.notificationType];
  };

  const categories = useMemo(() => {
    const grouped = new Map<string, NotificationTypeDto[]>();
    for (const type of types) {
      const existing = grouped.get(type.category);
      if (existing) {
        existing.push(type);
      } else {
        grouped.set(type.category, [type]);
      }
    }
    return grouped;
  }, [types]);

  if (loading) {
    return (
      <div
        className="flex min-h-[400px] items-center justify-center"
        data-testid="notification-testing-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6" data-testid="notification-testing-error">
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="notification-testing-page">
      <div className="mb-6 flex items-center gap-2">
        <NotificationsIcon aria-hidden="true" className="size-6" />
        <Text variant="h5">Notification Testing</Text>
      </div>

      <Text as="div" variant="body2" color="secondary" className="mb-4">
        Select a notification type, pick the target entity, then trigger it. The
        notification routes through the full notification service pipeline.
      </Text>

      <label
        htmlFor="force-unique-dedup-toggle"
        className="mb-4 flex cursor-pointer items-center gap-2"
      >
        <Switch
          id="force-unique-dedup-toggle"
          checked={forceUniqueDedup}
          onCheckedChange={setForceUniqueDedup}
          data-testid="force-unique-dedup-toggle"
        />
        <Text as="span" variant="body2">
          Bypass Deduplication
        </Text>
      </label>

      <div className="flex flex-col gap-2">
        {[...categories.entries()].map(([category, categoryTypes]) => (
          <div key={category} data-testid={`notification-category-${category}`}>
            <Accordion>
              <AccordionItem
                value="types"
                title={
                  <span className="flex items-center gap-2">
                    <Text as="span" variant="body1" className="font-semibold">
                      {category}
                    </Text>
                    <Chip label={categoryTypes.length} size="sm" />
                  </span>
                }
              >
                <div className="flex flex-col gap-4">
                  {categoryTypes.map(type => {
                    const chipInfo = getRequiredInputLabel(type.requiredInput);
                    const options = getOptionsForInput(
                      type.requiredInput,
                      entityOptions
                    );
                    const needsEntitySelect =
                      type.requiredInput !== 'none' &&
                      type.requiredInput !== 'raw';

                    return (
                      <div
                        key={type.notificationType}
                        className="rounded-xl border border-border-default bg-surface-default p-4"
                        data-testid={`notification-type-${type.notificationType}`}
                      >
                        <div className="flex flex-col gap-4">
                          <div className="flex flex-col items-stretch gap-4 sm:flex-row sm:items-center">
                            <div className="flex-1">
                              <Text
                                as="div"
                                variant="body1"
                                className="font-semibold"
                              >
                                {formatTypeName(type.notificationType)}
                              </Text>
                              <div className="mt-1 flex flex-row gap-2">
                                {type.notificationType !== '_Raw' && (
                                  <Chip
                                    label={type.notificationType}
                                    size="sm"
                                    variant="outlined"
                                  />
                                )}
                                <Chip
                                  label={chipInfo.label}
                                  size="sm"
                                  color={chipInfo.color}
                                />
                              </div>
                              <Text
                                as="div"
                                variant="caption"
                                color="secondary"
                                className="mt-1 block"
                              >
                                {type.description}
                              </Text>
                            </div>

                            {needsEntitySelect && (
                              <div className="min-w-[340px] flex-1 sm:max-w-[340px]">
                                <Combobox
                                  options={options.map(option => ({
                                    label: option.label,
                                    value: option.id,
                                    description: `${option.status} — ${option.id.slice(0, 8)}...`,
                                  }))}
                                  value={
                                    selectedEntities[type.notificationType]?.id
                                  }
                                  onChange={value =>
                                    setSelectedEntities(prev => ({
                                      ...prev,
                                      [type.notificationType]:
                                        options.find(
                                          option => option.id === value
                                        ) ?? null,
                                    }))
                                  }
                                  label={`Select ${type.requiredInput}`}
                                  placeholder="Search..."
                                  data-testid={`entity-select-${type.notificationType}`}
                                />
                              </div>
                            )}

                            <Button
                              variant="neutral"
                              disabled={isSendDisabled(type)}
                              onClick={() => handleTrigger(type)}
                              data-testid={`trigger-btn-${type.notificationType}`}
                            >
                              {sendingType === type.notificationType ? (
                                <Spinner size={16} color="inherit" />
                              ) : (
                                <SendIcon
                                  aria-hidden="true"
                                  className="size-4"
                                />
                              )}
                              Send
                            </Button>
                          </div>

                          {type.requiredInput === 'raw' && (
                            <div className="flex flex-col gap-3">
                              <Field label="Notification Type">
                                <Input
                                  placeholder="e.g. PotApprovedNotificationV1"
                                  value={rawTypes[type.notificationType] || ''}
                                  onChange={e =>
                                    setRawTypes(prev => ({
                                      ...prev,
                                      [type.notificationType]: e.target.value,
                                    }))
                                  }
                                  data-testid="raw-notification-type-input"
                                />
                              </Field>
                              <Field label="JSON Data (optional)">
                                <Textarea
                                  placeholder='{"key": "value"}'
                                  value={rawData[type.notificationType] || ''}
                                  onChange={e =>
                                    setRawData(prev => ({
                                      ...prev,
                                      [type.notificationType]: e.target.value,
                                    }))
                                  }
                                  rows={2}
                                  data-testid="raw-notification-data-input"
                                />
                              </Field>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </AccordionItem>
            </Accordion>
          </div>
        ))}
      </div>
    </div>
  );
};
